import { IRankingItem, ITableRow, TPlatform, IGameInfo, IRankingData } from './types';

/**
 * 转换表格数据为下载格式（支持合并单元格）
 */
export const transformDownloadData = (
  data: ITableRow[],
  games: IGameInfo[],
  platform: TPlatform,
  isEN: boolean
): {
  columns: Array<{ code: string; name: string; }>;
  data: any[];
  headerRows: any[][];
  mergeCells: Array<{ s: { r: number; c: number; }; e: { r: number; c: number; }; }>;
} => {
  const platformLabel = platform === 'appStore' ? 'AppStore' : 'GooglePlay';

  const downloadColumns: Array<{ code: string; name: string; }> = [
    {
      code: 'country',
      name: isEN ? '国家' : '国家',
    },
  ];

  games.forEach((game) => {
    downloadColumns.push(
      {
        code: `${game.name}_免费榜_本周`,
        name: isEN ? '免费榜' : '免费榜',
      },
      {
        code: `${game.name}_免费榜_上周`,
        name: isEN ? '免费榜' : '免费榜',
      },
      {
        code: `${game.name}_免费榜_变化`,
        name: isEN ? '变化' : '变化',
      },
      {
        code: `${game.name}_畅销榜_本周`,
        name: isEN ? '畅销榜' : '畅销榜',
      },
      {
        code: `${game.name}_畅销榜_上周`,
        name: isEN ? '畅销榜' : '畅销榜',
      },
      {
        code: `${game.name}_畅销榜_变化`,
        name: isEN ? '变化' : '变化',
      }
    );
  });

  const firstHeaderRow: string[] = [isEN ? '国家' : '国家'];
  games.forEach((game) => {
    firstHeaderRow.push(game.name, '', '', '', '', '');
  });

  const secondHeaderRow: string[] = [''];
  games.forEach(() => {
    secondHeaderRow.push(
      `${isEN ? '本周' : '本周'} ${platformLabel} ${isEN ? '免费榜' : '免费榜'}`,
      `${isEN ? '上周' : '上周'} ${platformLabel} ${isEN ? '免费榜' : '免费榜'}`,
      isEN ? '变化' : '变化',
      `${isEN ? '本周' : '本周'} ${platformLabel} ${isEN ? '畅销榜' : '畅销榜'}`,
      `${isEN ? '上周' : '上周'} ${platformLabel} ${isEN ? '畅销榜' : '畅销榜'}`,
      isEN ? '变化' : '变化'
    );
  });

  const mergeCells: Array<{ s: { r: number; c: number; }; e: { r: number; c: number; }; }> = [];

  mergeCells.push({
    s: { r: 0, c: 0 },
    e: { r: 1, c: 0 },
  });

  let mergeColIndex = 1;
  games.forEach(() => {
    mergeCells.push({
      s: { r: 0, c: mergeColIndex },
      e: { r: 0, c: mergeColIndex + 5 },
    });
    mergeColIndex += 6;
  });

  const downloadData = data.map((row) => {
    const downloadRow: any = {
      country: row.country,
    };

    games.forEach((game) => {
      const freeRankData = row[`${game.name}_免费榜_data`] as IRankingData | undefined;
      const revenueRankData = row[`${game.name}_畅销榜_data`] as IRankingData | undefined;

      const formatChange = (changeValue: number | undefined) => {
        if (!changeValue || changeValue === 0) return '-';
        if (changeValue > 0) return `↑${changeValue}`;
        return `↓${Math.abs(changeValue)}`;
      };

      downloadRow[`${game.name}_免费榜_本周`] = freeRankData?.compareDateRank || '-';
      downloadRow[`${game.name}_免费榜_上周`] = freeRankData?.baseDateRank || '-';
      downloadRow[`${game.name}_免费榜_变化`] = formatChange(freeRankData?.compareChange);
      downloadRow[`${game.name}_畅销榜_本周`] = revenueRankData?.compareDateRank || '-';
      downloadRow[`${game.name}_畅销榜_上周`] = revenueRankData?.baseDateRank || '-';
      downloadRow[`${game.name}_畅销榜_变化`] = formatChange(revenueRankData?.compareChange);
    });

    return downloadRow;
  });

  return {
    columns: downloadColumns,
    data: downloadData,
    headerRows: [firstHeaderRow, secondHeaderRow],
    mergeCells,
  };
};

export const getPlatformDims = (platform: TPlatform, map: Record<TPlatform, string[]>) => {
  return map?.[platform] || map.googlePlay;
};

/**
 * 转换接口返回的扁平列表为按国家分组的表格数据
 */
export const transformRankingData = (
  list: IRankingItem[],
  platform: TPlatform,
  gameNameMap: Record<string, string>,
  platformDimMap: Record<TPlatform, string[]>,
): ITableRow[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  const platformRankTypes = getPlatformDims(platform, platformDimMap);

  const filteredList = list.filter((item) => platformRankTypes.includes(item.rank_type));

  const countryMap = new Map<string, ITableRow>();

  filteredList.forEach((item) => {
    const {
      country,
      country_code: countryCode,
      game_name: gameName,
      rank_type: rankType,
      base_date_rank: baseDateRank,
      compare_date_rank: compareDateRank,
      compare_change: compareChange,
      compare_rate: compareRate,
      base_date: baseDate,
      compare_date: compareDate
    } = item;

    if (!countryMap.has(country)) {
      countryMap.set(country, {
        country,
        country_code: countryCode,
        countryCode,
      });
    }

    const row = countryMap.get(country)!;
    const rankTypeKey = rankType.includes('free') ? '免费榜' : '畅销榜';
    const mappedGameName = gameNameMap[gameName] || gameName;
    const columnKey = `${mappedGameName}_${rankTypeKey}`;
    const dataKey = `${columnKey}_data`;

    const rankingData: IRankingData = {
      baseDateRank: Number(baseDateRank) || 0,
      compareDateRank: Number(compareDateRank) || 0,
      compareChange: Number(compareChange) || 0,
      compareRate: Number(compareRate) || 0,
      baseDate: baseDate || '',
      compareDate: compareDate || '',
    };

    row[dataKey] = rankingData;
    row[columnKey] = rankingData.compareDateRank;
  });

  return Array.from(countryMap.values());
};
